import { Component } from '@theme/component'
import '@theme/gsap'
import { clamp, lerp, useReduced } from '@theme/maths'

const IngredientsCarouselConfig = {
  card: '.ingredients-carousel__card',
  slide: '.ingredients-carousel__slide',
  toggle: '[data-ingredient-toggle]',
  step: '[data-ingredient-step]',
  scaleLadder: [2.5, 1.5, 1.1134, 0.7273],
  idleScale: 0.6,
  idleOpacity: 0.5,
  fadeStart: 3,
  fadeEnd: 4,
  minSlides: 9,
  loopableSlides: 3,
  duration: 0.7,
  settleDuration: 0.45,
  ease: 'power4.out',
  dragSlop: 6,
  velocitySmoothing: 0.35,
  flickVelocity: 0.6,
  maxCommit: 2
}

/**
 * @typedef {object} SlideMetrics
 * @property {number} cardWidth - Unscaled card width in pixels.
 * @property {number} gap - Padding each slide carries on one side, in pixels.
 * @property {number} overlap - Pixels every neighbour is pulled back under the active card.
 * @property {number} activeStep - Distance between the active card and its neighbour.
 */

/**
 * @param {number} rung - Whole-numbered distance from the active slide.
 * @returns {number} Scale for that rung of the ladder, floored for far slides.
 */
function rungScale(rung) {
  const { scaleLadder, idleScale } = IngredientsCarouselConfig
  return scaleLadder[rung] ?? idleScale
}

/**
 * @param {number} distance - Signed, possibly fractional distance from the active slide.
 * @returns {number} Scale interpolated between the two rungs the distance falls between.
 */
function slideScale(distance) {
  const span = Math.abs(distance)
  const rung = Math.floor(span)
  return lerp(rungScale(rung), rungScale(rung + 1), span - rung)
}

/**
 * @param {number} distance - Signed, possibly fractional distance from the active slide.
 * @returns {number} Opacity that holds the reference ladder, then fades the outermost rung out.
 */
function slideOpacity(distance) {
  const { idleOpacity, fadeStart, fadeEnd } = IngredientsCarouselConfig
  const span = Math.abs(distance)
  const focus = lerp(idleOpacity, 1, clamp(0, 1 - span, 1))
  const fade = clamp(0, (fadeEnd - span) / (fadeEnd - fadeStart), 1)
  return focus * fade
}

/**
 * @param {number} delta
 * @param {number} count
 * @returns {number} Signed distance folded into the shortest way round.
 */
function wrapDistance(delta, count) {
  const half = count / 2
  return (((delta % count) + count + half) % count) - half
}

/**
 * @param {number} rung - Whole-numbered distance from the active slide.
 * @param {SlideMetrics} metrics
 * @returns {number} Unsigned pixel distance between the two card centres.
 */
function rungOffset(rung, metrics) {
  if (rung === 0) return 0

  const { cardWidth, gap, overlap } = metrics
  let reach = (cardWidth * rungScale(0)) / 2 + gap * 2

  for (let step = 1; step < rung; step += 1) {
    reach += cardWidth * rungScale(step) + gap * 2
  }

  return reach + (cardWidth * rungScale(rung)) / 2 - overlap
}

/**
 * @param {number} distance - Signed, possibly fractional distance from the active slide.
 * @param {SlideMetrics} metrics
 * @returns {number} Signed pixel offset, interpolated between whole rungs.
 */
function slideOffset(distance, metrics) {
  if (distance === 0) return 0

  const span = Math.abs(distance)
  const rung = Math.floor(span)
  const reach = lerp(
    rungOffset(rung, metrics),
    rungOffset(rung + 1, metrics),
    span - rung
  )

  return Math.sign(distance) * reach
}

/**
 * @param {number} velocity - Smoothed drag velocity in pixels per millisecond.
 * @returns {number} One slide in the thrown direction, or zero below the threshold.
 */
function flickStep(velocity) {
  const { flickVelocity } = IngredientsCarouselConfig
  if (Math.abs(velocity) < flickVelocity) return 0
  return velocity < 0 ? 1 : -1
}

/**
 * @param {HTMLElement} slide - One of the rendered slides.
 * @returns {HTMLElement} Inert copy that pads the loop; carries no theme-editor identity.
 */
function cloneSlide(slide) {
  const clone = /** @type {HTMLElement} */ (slide.cloneNode(true))
  clone.removeAttribute('id')
  for (const { name } of [...clone.attributes]) {
    if (name.startsWith('data-shopify')) clone.removeAttribute(name)
  }
  return clone
}

/**
 * @typedef {object} DragState
 * @property {number} x - Last pointer X in client pixels.
 * @property {number} travel - Signed pixels moved since the pointer went down.
 * @property {boolean} moved - Whether travel passed the click-suppression slop.
 * @property {number} velocity - Smoothed drag velocity in pixels per millisecond.
 * @property {number} time - Timestamp of the last pointer sample, in milliseconds.
 */

class IngredientsCarousel extends Component {
  #active = 0
  #progress = 0
  #suppressClick = false
  /** @type {DragState | null} */
  #drag = null
  /** @type {SlideMetrics | null} */
  #metricsCache = null
  /** @type {number[]} */
  #distances = []
  #resizeObserver = null

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    if (this.#suppressClick) {
      this.#suppressClick = false
      return
    }
    if (!(event.target instanceof Element)) return

    const stepButton = event.target.closest(IngredientsCarouselConfig.step)
    if (stepButton instanceof HTMLElement) {
      this.step(Number(stepButton.dataset.ingredientStep) || 0)
      return
    }

    const slide = event.target.closest(IngredientsCarouselConfig.slide)
    if (!(slide instanceof HTMLElement)) return

    if (!slide.hasAttribute('data-active')) {
      this.goTo(this.slides.indexOf(slide))
      return
    }

    if (event.target.closest(IngredientsCarouselConfig.toggle)) {
      this.#toggleDetails(slide)
    }
  }

  /** @param {PointerEvent} event */
  #onPointerDown = (event) => {
    if (event.button !== 0) return
    this.#suppressClick = false
    if (event.target instanceof Element) {
      if (event.target.closest(IngredientsCarouselConfig.step)) return
    }

    this.#drag = {
      x: event.clientX,
      travel: 0,
      moved: false,
      velocity: 0,
      time: event.timeStamp
    }
    this.toggleAttribute('data-dragging', true)
  }

  /** @param {PointerEvent} event */
  #onPointerMove = (event) => {
    const drag = this.#drag
    const metrics = this.#metrics()
    if (!(drag && metrics)) return

    const delta = event.clientX - drag.x
    drag.x = event.clientX
    drag.travel += delta
    this.#trackVelocity(drag, delta, event.timeStamp)
    if (Math.abs(drag.travel) <= IngredientsCarouselConfig.dragSlop) return

    if (!drag.moved) {
      drag.moved = true
      this.#collapseAll()
    }

    this.#progress = -drag.travel / metrics.activeStep
    this.#layout(0)
  }

  #onPointerUp = () => {
    this.removeAttribute('data-dragging')
    const drag = this.#drag
    this.#drag = null
    if (!drag) return

    this.#suppressClick = drag.moved
    if (!drag.moved) return

    const { maxCommit, settleDuration } = IngredientsCarouselConfig
    const dragged = Math.round(this.#progress)
    const commit = dragged === 0 ? flickStep(drag.velocity) : dragged
    const target = this.#active + clamp(-maxCommit, commit, maxCommit)
    this.goTo(target, settleDuration)
  }

  connectedCallback() {
    super.connectedCallback()
    this.#fillLoop()
    this.addEventListener('click', this.#onClick)
    this.addEventListener('pointerdown', this.#onPointerDown)
    this.addEventListener('pointermove', this.#onPointerMove)
    this.addEventListener('pointerup', this.#onPointerUp)
    this.addEventListener('pointercancel', this.#onPointerUp)
    this.#resizeObserver = new ResizeObserver(() => {
      this.#metricsCache = null
      this.#layout(0)
    })
    this.#resizeObserver.observe(this)
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    this.removeEventListener('click', this.#onClick)
    this.removeEventListener('pointerdown', this.#onPointerDown)
    this.removeEventListener('pointermove', this.#onPointerMove)
    this.removeEventListener('pointerup', this.#onPointerUp)
    this.removeEventListener('pointercancel', this.#onPointerUp)
    this.#resizeObserver?.disconnect()
  }

  /** @returns {HTMLElement[]} */
  get slides() {
    const { slides } = this.refs
    return Array.isArray(slides) ? slides : []
  }

  /**
   * @param {number} index
   * @param {number} [duration] - Tween length in seconds; defaults to the navigation duration.
   */
  goTo(index, duration = IngredientsCarouselConfig.duration) {
    const count = this.slides.length
    if (count === 0) return

    const next = ((index % count) + count) % count
    if (next === this.#active && this.#progress === 0) return

    if (next !== this.#active) this.#collapseAll()
    this.#active = next
    this.#progress = 0
    this.#layout(useReduced() ? 0 : duration)
  }

  /** @param {number} direction */
  step(direction) {
    this.goTo(this.#active + direction)
  }

  #fillLoop() {
    const track = this.refs.track
    const originals = this.slides
    const { minSlides, loopableSlides } = IngredientsCarouselConfig
    if (!(track instanceof HTMLElement)) return
    if (originals.length < loopableSlides || originals.length >= minSlides) {
      return
    }

    const passes = Math.ceil(minSlides / originals.length) - 1
    for (let pass = 0; pass < passes; pass += 1) {
      for (const slide of originals) track.append(cloneSlide(slide))
    }
    this.updatedCallback()
  }

  /** @returns {SlideMetrics | null} */
  #metrics() {
    if (this.#metricsCache) return this.#metricsCache

    const slide = this.slides[0]
    const card = slide?.querySelector(IngredientsCarouselConfig.card)
    if (!(slide instanceof HTMLElement && card instanceof HTMLElement)) {
      return null
    }

    const cardWidth = card.offsetWidth
    const gap = Math.max(0, (slide.offsetWidth - cardWidth) / 2)
    const base = {
      cardWidth,
      gap,
      overlap: cardWidth * this.#overlapRatio(),
      activeStep: 0
    }
    base.activeStep = rungOffset(1, base)
    this.#metricsCache = base

    return base
  }

  /** @returns {number} Fraction of a card width every neighbour tucks behind the active one. */
  #overlapRatio() {
    const declared = getComputedStyle(this).getPropertyValue(
      '--ingredients-carousel-overlap'
    )
    return clamp(0, Number.parseFloat(declared) || 0, 2)
  }

  /** @param {number} duration - Tween length in seconds; zero paints instantly. */
  #layout(duration) {
    const metrics = this.#metrics()
    const slides = this.slides
    if (!metrics || slides.length === 0) return

    const origin = this.#active + this.#progress
    for (const [index, slide] of slides.entries()) {
      const distance = wrapDistance(index - origin, slides.length)
      const previous = this.#distances[index]
      const wrapped =
        previous !== undefined && Math.abs(distance - previous) > 1
      this.#paintSlide(slide, distance, metrics, wrapped ? 0 : duration)
      this.#distances[index] = distance
    }
  }

  /**
   * @param {HTMLElement} slide
   * @param {number} distance
   * @param {SlideMetrics} metrics
   * @param {number} duration
   */
  #paintSlide(slide, distance, metrics, duration) {
    const card = slide.querySelector(IngredientsCarouselConfig.card)
    const span = Math.abs(distance)
    const isActive = span < 0.5

    slide.toggleAttribute('data-active', isActive)
    slide.style.zIndex = String(this.slides.length - Math.round(span))
    const toggle = slide.querySelector(IngredientsCarouselConfig.toggle)
    if (toggle instanceof HTMLElement) toggle.tabIndex = isActive ? 0 : -1
    const scale = slideScale(distance)
    this.#sizeSlideImage(slide, metrics.cardWidth * scale)
    this.#apply(
      slide,
      {
        x: slideOffset(distance, metrics),
        '--ingredients-carousel-scale': scale
      },
      duration
    )
    this.#apply(card, { scale, opacity: slideOpacity(distance) }, duration)
  }

  /**
   * @param {HTMLElement} slide
   * @param {number} renderedWidth - Card width after scaling, in CSS pixels.
   */
  #sizeSlideImage(slide, renderedWidth) {
    const image = slide.querySelector('img')
    if (!(image instanceof HTMLImageElement)) return
    image.sizes = `${Math.round(renderedWidth)}px`
  }

  /**
   * @param {Element | null} target
   * @param {object} vars
   * @param {number} duration
   */
  #apply(target, vars, duration) {
    const gsap = window.gsap
    if (!(target instanceof HTMLElement && gsap)) return

    if (duration === 0) {
      gsap.set(target, vars)
      return
    }

    gsap.to(target, {
      ...vars,
      duration,
      ease: IngredientsCarouselConfig.ease,
      overwrite: 'auto'
    })
  }

  /** @param {HTMLElement} slide */
  #toggleDetails(slide) {
    const toggle = slide.querySelector(IngredientsCarouselConfig.toggle)
    if (!(toggle instanceof HTMLElement)) return

    const expanded = slide.toggleAttribute('data-expanded')
    toggle.setAttribute('aria-expanded', String(expanded))
    const label = expanded
      ? toggle.dataset.labelCollapse
      : toggle.dataset.labelExpand
    toggle.setAttribute('aria-label', label ?? '')
  }

  #collapseAll() {
    for (const slide of this.slides) {
      if (slide.hasAttribute('data-expanded')) this.#toggleDetails(slide)
    }
  }

  /**
   * @param {DragState} drag
   * @param {number} delta - Pixels moved since the previous sample.
   * @param {number} now - Current pointer timestamp in milliseconds.
   */
  #trackVelocity(drag, delta, now) {
    const dt = Math.max(1, now - drag.time)
    drag.time = now
    const { velocitySmoothing } = IngredientsCarouselConfig
    drag.velocity = lerp(drag.velocity, delta / dt, velocitySmoothing)
  }
}

if (!customElements.get('ingredients-carousel')) {
  customElements.define('ingredients-carousel', IngredientsCarousel)
}
